'use client';
import React from 'react';
import { Text, Icon } from '@benzinga/core-ui';
import styles from './StackedCards.module.scss';
import { SmartLink } from '@benzinga/analytics';
import { BzImage } from '@benzinga/image';
import { CallToActionButton } from '@benzinga/ui';
import type { ProductBasedCTAProps, ProductBasedCTALayout } from './ProductBasedCTA';
import { withYmailParam } from './withYmailParam';
import { StackedCardHeader } from './StackedCardHeader';
import { faChevronsDown, faChevronsUp } from '@fortawesome/pro-regular-svg-icons';
import Hooks from '@benzinga/hooks';
import { Tooltip } from '@benzinga/core-ui';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

export interface StackedCardsProps {
  groups?: ProductBasedCTAProps[];
  layout?: ProductBasedCTALayout;
  social_proofing?: string;
  heading?: string;
  compact_mode?: boolean;
  initialItems?: number;
  location?: string;
}

export const StackedCards: React.FC<StackedCardsProps> = ({
  compact_mode = false,
  groups,
  heading,
  initialItems,
  layout,
  location = 'default',
  social_proofing,
}) => {
  const { width } = Hooks.useWindowSize();
  const isMobile = width < 640;

  const [showAll, setShowAll] = React.useState<boolean>(false);
  const items = groups ?? [];
  let splicedItems = compact_mode
    ? items.slice(0, 3)
    : initialItems && initialItems !== -1 && initialItems > 0
      ? [...items].splice(0, initialItems ?? items.length)
      : items;

  if (isMobile && compact_mode) {
    splicedItems = items.slice(0, 2);
  }

  const handleShowAll = React.useCallback(() => {
    setShowAll(!showAll);
  }, [showAll]);

  const data = showAll ? items : splicedItems;
  const isSidebar = location === 'sidebar';

  return (
    <>
      {social_proofing || heading ? <StackedCardHeader heading={heading} social_proofing={social_proofing} /> : null}
      <div
        className={`${styles.wrapper} ${items.length > 1 ? 'p-3' : 'p-1'} ${layout ?? ''} ${compact_mode ? styles.compact : ''}`}
      >
        {data &&
          data.map((campaign, index) => {
            if (!campaign?.url) return null;

            const href = withYmailParam(campaign.url);

            return (
              <div
                className={`${styles.callToActionContainer} relative flex flex-col ${!isSidebar ? 'sm:flex-row' : ''} gap-4 w-full justify-between items-center`}
                key={index}
              >
                <div
                  className={`${!isSidebar ? 'sm:w-1/3' : ''} w-full promo-image-wrap flex items-center justify-center`}
                >
                  {campaign?.image_url && (
                    <div className={styles.imageWrapper}>
                      <BzImage alt={campaign.title} height={75} src={campaign.image_url} width={120} />
                    </div>
                  )}
                </div>
                <div className={`${!isSidebar ? 'flex sm:w-2/3' : ''} w-full  justify-center call-to-action-copy`}>
                  <div>
                    <div className={`${styles.title} text-center`}>{campaign.title} </div>
                    {!compact_mode && campaign?.description && (
                      <Text
                        className={styles.callToActionDescription}
                        dangerouslySetInnerHTML={{ __html: campaign?.description }}
                        lines={5}
                      />
                    )}
                    {!compact_mode && !campaign?.disable_disclosure && campaign?.review_url && (
                      <small className={`flex flex-row pb-3 text-gray-400 justify-center`}>
                        {campaign?.review && <span className="pr-1">{campaign?.review}</span>}
                        {campaign?.review_url && (
                          <a
                            className={` disclosure-link text-gray-400 underline`}
                            href={campaign?.review_url}
                            target="_blank"
                          >
                            (17b disclosure)
                          </a>
                        )}
                      </small>
                    )}
                    <SmartLink action="click" category={location} href={href}>
                      <CallToActionButton text={campaign?.button_text} variant="flat-blue" />
                    </SmartLink>
                  </div>
                </div>
                {campaign?.tooltip && (
                  <div className={styles.cardToolTip}>
                    <Tooltip content={campaign?.tooltip} isContentHtml={true} width={250}>
                      <Icon
                        className={`${styles.infoCircleIcon} text-sm flex items-center justify-center`}
                        icon={faCircleInfo}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
            );
          })}
        {!compact_mode && initialItems && initialItems != -1 && items.length > initialItems && (
          <div className="view-more-wrap text-right mt-2 cursor-pointer" onClick={handleShowAll}>
            <span className={`${styles.viewMore} flex items-center justify-center gap-1 text-md underline pt-2`}>
              {showAll ? (
                <>
                  View Less
                  <Icon className={styles.iconWrap} icon={faChevronsUp} />
                </>
              ) : (
                <>
                  View More
                  <Icon className={styles.iconWrap} icon={faChevronsDown} />
                </>
              )}
            </span>
          </div>
        )}
      </div>
    </>
  );
};
